<template>
  <div>
    <div>
      <div class="flex flex-col mb-4">
        <p class="checklist-builder__second-stage-title">
          {{ $t('template-builder.access-editors.title') }}
        </p>
        <p class="text-sm text-70">
          {{ $t('template-builder.access-editors.description') }}
        </p>
      </div>
    </div>

    <div v-if="!isOnFreePlan">
      <BigField
        :class="editableByAll ? 'checklist-builder__big-field-users--all-selected' : ''"
      >
        <template v-slot:field>
          <checkbox-field
            class="checklist-builder__second-stage-editors-all-users-checkbox"
            :value="editableByAll"
            :label="$t('template-builder.access-editors.all-users')"
            @input="toggleEditableByAll"
          />
        </template>
      </BigField>

      <div v-if="!editableByAll">
        <choose-user-field-with-select-all
          :hide-label="true"
          :options="options"
          :disabled="isOnFreePlan"
          :value="checklistEditorsOptions"
          :errors="[]"
          :identifier="createUserModelIdentifier.checklistBuilderUserEditor"
          :select-all-enabled="true"
          :deselect-all-enabled="true"
          @input="setEditorOptions"
        />
      </div>
    </div>

    <div v-else>
      <upgrade-ad :message="$t('upgrade-ad.cb-stage2')" is-vertical with-dropdown :placeholder="$t('All users')" />
    </div>
  </div>
</template>

<script>
  import _ from 'lodash';
  import BigField from '@/_legacy/views/ChecklistBuilder/BigField.vue';
  import SelectControl from '@/_legacy/components/Controls/SelectControl.vue';
  import authMixin from '@/_legacy/mixins/authMixin';
  import { eventBus } from '@/_legacy/eventBus';
  import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
  import ChooseUserFieldWithSelectAll from '@/ui/components/user/ChooseUserFieldWithSelectAll';
  import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
  import { CheckboxField } from '@/ui/components/form';
  import { Icon } from '@/ui/components/icon/Icon';
  import { filterListToEditableGroups, filterListToEditableUsers, separateUsersAndGroups } from '@/ui/helpers';

  export default {
    components: {
      CheckboxField,
      UpgradeAd,
      BigField,
      SelectControl,
      ChooseUserFieldWithSelectAll,
      Icon,
    },

    mixins: [
      authMixin,
    ],

    props: {
      checklistEditors: {
        type: Object,
        default: null,
      },
      groups: {
        type: Array,
        required: true,
      },
      users: {
        type: Array,
        required: true,
      }
    },

    data() {
      return {
        freePlanModalMessage: {
          text: this.$t('This feature is not included in your free plan.'),
          advice: this.$t('Upgrade now to disallow editing this checklist from specific users or groups'),
        },
        editors: {
          groups: [],
          users: []
        },
        createUserModelIdentifier: CreateUserModelIdentifier,
        unWatchOptions: null,
        mergedOptions: [],
      };
    },

    computed: {
      everybodyCanEdit() {
        return this.$store.getters['checklist_builder/everybodyCanEdit'];
      },

      options() {
        const users = this.users.map(user => ({ ...user, value: `user_${user.value}` }));
        const groups = this.groups.map(group => ({ ...group, value: `group_${group.value}` }));

        const finalOptions = [...users, ...groups];

        this.mergedOptions = finalOptions;

        return finalOptions;
      },

      checklistEditorsOptions() {
        const { users, groups } = this.editors;

        return this.getOptions(users, groups);
      },

      checklist() {
        return this.$store.getters['checklist_builder/checklist'];
      },

      editableByAll() {
        return this.checklist.editableByAll;
      },
    },

    mounted() {
      this.editors = this.checklistEditors || {
        groups: [],
        users: []
      };
    },

    methods: {
      selectAllUsers() {
        const options = this.getOptions(this.users, this.groups, this.sites).map(option => option.value);
        this.setEditorOptions(options);
      },
      deselectAllUsers() {
        this.setEditorOptions([]);
      },

      toggleEditableByAll() {
        this.$store.commit('checklist_builder/setEditableByAll', !this.editableByAll);
      },

      onUserCreated({ response, identifier }) {
        if (CreateUserModelIdentifier.checklistBuilderUserRestriction !== identifier) {
          return;
        }

        this.setEditorOptions(options.value.push(`user_${response.id}`));
      },

      displayFreePlanModal() {
        eventBus.$emit('free-plan-limit', { message: this.freePlanModalMessage });
      },

      setEditorOptions(options) {
        this.$store.commit('checklist_builder/everybodyCanEdit', _.isEmpty(options));

        let { users, groups } = separateUsersAndGroups(options);

        users = filterListToEditableUsers(users).map(id => Number(id));
        groups = filterListToEditableGroups(groups).map(id => Number(id));

        this.$set(this.editors, 'users', users);
        this.$set(this.editors, 'groups', groups);

        !users.length && this.$set(this.editors, 'users', []);
        !groups.length && this.$set(this.editors, 'groups', []);

        if (!this.editableByAll) {
          this.$store.commit('checklist_builder/setEditors', this.editors);
        }
      },

      getOptions(users, groups) {
        users = users.map(user => `user_${user}`);
        groups = groups.map(group => `group_${group}`);

        return [...users, ...groups];
      },
    }
  };
</script>
